import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Cloud, Database, Eye, Hash, Lock, Mail, Share2, Shield, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: <Shield className="w-7 h-7 text-primary" />,
      title: 'AES-256-GCM Encryption',
      description: 'The same standard used by defence agencies. Client-side only.',
    },
    {
      icon: <Mail className="w-7 h-7 text-primary" />,
      title: 'Passwordless Login',
      description: 'OTP via email. No passwords to remember, steal, or forget.',
    },
    {
      icon: <Share2 className="w-7 h-7 text-primary" />,
      title: 'Secure File Sharing',
      description: 'Time-limited links with optional passwords. Share without an account.',
    },
    {
      icon: <Eye className="w-7 h-7 text-primary" />,
      title: 'Suspicious Activity Alerts',
      description: 'Automated detection of unusual access patterns. You stay in control.',
    },
    {
      icon: <Hash className="w-7 h-7 text-primary" />,
      title: 'File Integrity Verification',
      description: 'SHA-256 hashing confirms your files are untampered on every download.',
    },
    {
      icon: <Database className="w-7 h-7 text-primary" />,
      title: 'Complete Audit Trail',
      description: 'Every action logged. Full forensic traceability for your files.',
    },
  ];

  const comparisonRows = [
    ['Client-side encryption', 'yes', 'no', 'no'],
    ['Zero-knowledge', 'yes', 'no', 'no'],
    ['Passwordless auth', 'yes', 'no', 'no'],
    ['Free audit logs', 'yes', 'no', 'no'],
    ['Share link expiry', 'yes', 'Limited', 'Limited'],
    ['Open architecture', 'yes', 'no', 'no'],
  ];

  const renderComparisonValue = (value) => {
    if (value === 'yes') {
      return <Check className="w-5 h-5 text-[hsl(var(--success))] mx-auto" />;
    }
    if (value === 'no') {
      return <X className="w-5 h-5 text-destructive mx-auto" />;
    }
    return <span className="text-sm text-muted-foreground">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="sticky top-0 z-50 backdrop-blur-sm bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <Shield className="w-8 h-8 text-primary" />
            <span className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>SecureVault</span>
          </Link>
          <Link to="/login">
            <Button data-testid="login-nav-btn">Sign In</Button>
          </Link>
        </div>
      </nav>

      <section className="relative overflow-hidden bg-gradient-to-b from-background to-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hero-title">
              Your Files. Encrypted. Private. Yours.
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8">
              Military-grade AES-256-GCM encryption happens entirely in your browser. Your files are encrypted before they leave your device. Not even we can read them.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Link to="/login" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto" data-testid="hero-cta-btn">
                  Start Securing Files Free
                </Button>
              </Link>
              <Button size="lg" variant="outline" onClick={scrollToHowItWorks} className="w-full sm:w-auto" data-testid="how-it-works-btn">
                See How It Works
              </Button>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs sm:text-sm text-muted-foreground">
              <span>🔒 Zero-Knowledge</span>
              <span>🛡️ AES-256-GCM</span>
              <span>✓ No Credit Card</span>
              <span>🌐 Open Source Ready</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-muted/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
            <div className="py-6 text-center">
              <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>256-bit</p>
              <p className="text-sm text-muted-foreground mt-1">Encryption Key Length</p>
            </div>
            <div className="py-6 text-center">
              <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>0</p>
              <p className="text-sm text-muted-foreground mt-1">Files We Can Read</p>
            </div>
            <div className="py-6 text-center">
              <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>8</p>
              <p className="text-sm text-muted-foreground mt-1">Security Layers</p>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Zero-Knowledge Security, Step by Step
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold mb-5">1</div>
              <Upload className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>You Select a File</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Your file is read locally in your browser. It never leaves your device unencrypted.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold mb-5">2</div>
              <Lock className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Encrypted in Your Browser</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">AES-256-GCM encryption runs using your browser's built-in Web Cryptography API. The key is generated fresh for every file.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold mb-5">3</div>
              <Cloud className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Only Ciphertext Reaches Our Server</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">We store encrypted binary data. Without your key, it's mathematically indecipherable, even to us.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need. Nothing You Don't.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="bg-card border border-border rounded-lg p-6">
                <div className="mb-5">{feature.icon}</div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How We Compare
            </h2>
          </div>
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">Feature</th>
                  <th className="px-4 sm:px-6 py-4 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">SecureVault</th>
                  <th className="px-4 sm:px-6 py-4 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">Google Drive</th>
                  <th className="px-4 sm:px-6 py-4 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">Dropbox</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {comparisonRows.map(([feature, secureVault, googleDrive, dropbox]) => (
                  <tr key={feature}>
                    <td className="px-4 sm:px-6 py-4 text-sm font-medium">{feature}</td>
                    <td className="px-4 sm:px-6 py-4 text-center">{renderComparisonValue(secureVault)}</td>
                    <td className="px-4 sm:px-6 py-4 text-center">{renderComparisonValue(googleDrive)}</td>
                    <td className="px-4 sm:px-6 py-4 text-center">{renderComparisonValue(dropbox)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-card border-y border-border py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Take Control of Your Files?
          </h2>
          <p className="text-muted-foreground mb-8">Free forever. No credit card. No hidden limits on encryption.</p>
          <Link to="/login" className="inline-block w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto" data-testid="final-cta-btn">
              Create Your Vault — It's Free
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border pt-8 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>SecureVault</span>
            </div>
            <p className="text-sm text-muted-foreground">© 2025 SecureVault. Built with zero-knowledge principles.</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors duration-150">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors duration-150">Terms</Link>
            <Link to="/security-disclosure" className="hover:text-foreground transition-colors duration-150">Security</Link>
            <a href="mailto:security@priyanshurathod.com" className="hover:text-foreground transition-colors duration-150">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
